import React, { useEffect, useRef } from "react"
import { createPortal } from "react-dom"

// BDSJEA-468: fuegos artificiales en <canvas> (sin librerías) con un texto
// centrado. Overlay fijo transparente a los eventos. Solo corre mientras está
// montado: al terminar (o desmontar) cancela el requestAnimationFrame y el timer.
export const DURACION_FUEGOS_MS = 3500
export const DURACION_TEXTO_REDUCIDO_MS = 3000
const COLORES = ["#ffd54f", "#ff7043", "#4fc3f7", "#81c784", "#ba68c8", "#fff"]

const prefiereMenosMovimiento = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  !!window.matchMedia("(prefers-reduced-motion: reduce)")?.matches

const estiloOverlay = {
  position: "fixed",
  inset: 0,
  zIndex: 20000,
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}
const estiloTexto = {
  position: "relative",
  fontSize: "2.5rem",
  fontWeight: 700,
  color: "#fff",
  textShadow: "0 2px 12px rgba(0,0,0,.8)",
  textAlign: "center",
}

const FuegosArtificiales = ({ texto, onFin }) => {
  const canvasRef = useRef(null)
  const reducido = prefiereMenosMovimiento()
  const onFinRef = useRef(onFin)
  onFinRef.current = onFin

  useEffect(() => {
    const terminar = () => onFinRef.current && onFinRef.current()
    if (reducido) {
      const t = setTimeout(terminar, DURACION_TEXTO_REDUCIDO_MS)
      return () => clearTimeout(t)
    }
    const canvas = canvasRef.current
    const ctx = canvas && canvas.getContext ? canvas.getContext("2d") : null
    if (!ctx) {
      const t = setTimeout(terminar, DURACION_FUEGOS_MS)
      return () => clearTimeout(t)
    }
    const ajustar = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    ajustar()
    window.addEventListener("resize", ajustar)

    let particulas = []
    let raf = null
    const inicio = Date.now()
    let proximo = 0
    const explotar = () => {
      const x = canvas.width * (0.15 + Math.random() * 0.7)
      const y = canvas.height * (0.15 + Math.random() * 0.5)
      const color = COLORES[Math.floor(Math.random() * COLORES.length)]
      for (let i = 0; i < 50; i += 1) {
        const ang = (Math.PI * 2 * i) / 50
        const vel = 1 + Math.random() * 4
        particulas.push({
          x,
          y,
          vx: Math.cos(ang) * vel,
          vy: Math.sin(ang) * vel,
          vida: 1,
          color,
        })
      }
    }
    const cuadro = () => {
      const t = Date.now() - inicio
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      if (t < DURACION_FUEGOS_MS - 1000 && t >= proximo) {
        explotar()
        proximo = t + 350
      }
      particulas = particulas.filter(p => p.vida > 0)
      particulas.forEach(p => {
        p.x += p.vx
        p.y += p.vy
        p.vy += 0.05
        p.vida -= 0.014
        ctx.globalAlpha = Math.max(p.vida, 0)
        ctx.fillStyle = p.color
        ctx.fillRect(p.x, p.y, 3, 3)
      })
      ctx.globalAlpha = 1
      if (t < DURACION_FUEGOS_MS) raf = requestAnimationFrame(cuadro)
      else terminar()
    }
    raf = requestAnimationFrame(cuadro)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener("resize", ajustar)
    }
  }, [reducido])

  return createPortal(
    <div
      style={estiloOverlay}
      data-testid="fuegos-artificiales"
      role="status"
      aria-live="polite"
    >
      {!reducido && (
        <canvas
          ref={canvasRef}
          style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          aria-hidden="true"
        />
      )}
      <div style={estiloTexto}>{texto}</div>
    </div>,
    document.body,
  )
}

export default FuegosArtificiales
